import React, { useEffect, useMemo, useRef, useState } from "react";
import "./banner.scss";
import { fetchBanner } from "../../api/newsService";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { ChevronLeft, ChevronRight } from "lucide-react";

function BannerCarousel({ items, variant }) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const canSlide = items.length > 1;

  const settings = useMemo(
    () => ({
      modules: [Autoplay, Pagination, Navigation],
      slidesPerView: 1,
      loop: canSlide,
      speed: 700,
      autoplay: canSlide
        ? {
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
      pagination: { clickable: true },
      navigation: canSlide
        ? { prevEl: prevRef.current, nextEl: nextRef.current }
        : false,
    }),
    [canSlide]
  );

  if (!items.length) return null;

  return (
    <div className={`banner-swiper ${variant}`}>
      {canSlide && (
        <>
          <button
            ref={prevRef}
            type="button"
            className="banner-swiper__arrow banner-swiper__arrow--prev"
            aria-label="Previous"
          >
            <ChevronLeft size={28} />
          </button>

          <button
            ref={nextRef}
            type="button"
            className="banner-swiper__arrow banner-swiper__arrow--next"
            aria-label="Next"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      <Swiper
        {...settings}
        className="banner-swiper__swiper"
        onBeforeInit={(swiper) => {
          if (!canSlide) return;
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
      >
        {items.map((post, idx) => (
          <SwiperSlide key={post?.id ?? idx}>
            <div className="banner-swiper__slide">
              <img
                src={post?.image}
                alt={post?.title || `banner-${idx + 1}`}
                loading="lazy"
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

const isMobileBanner = (item) =>
  String(item?.description || "").trim().toLowerCase() === "mobile";

export default function Banner() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const getData = async () => {
      const res = await fetchBanner();
      const data = res?.data?.data || [];
      setItems(Array.isArray(data) ? data : []);
    };
    getData();
  }, []);

  const { desktopItems, mobileItems } = useMemo(() => {
    return items.reduce(
      (groups, item) => {
        if (isMobileBanner(item)) {
          groups.mobileItems.push(item);
        } else {
          groups.desktopItems.push(item);
        }

        return groups;
      },
      { desktopItems: [], mobileItems: [] }
    );
  }, [items]);

  const desktopBanners = desktopItems;
  const mobileBanners = mobileItems;

  if (!desktopBanners.length && !mobileBanners.length) return null;

  return (
    <>
      <BannerCarousel
        items={desktopBanners}
        variant="banner-swiper--desktop"
      />
      <BannerCarousel
        items={mobileBanners}
        variant="banner-swiper--mobile"
      />
    </>
  );
}
